import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Box, List, Drawer, Button, Divider } from '@mui/material';
import { useQuery, useMutation } from 'react-fetching-library';
import { useSelector, useDispatch } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useGetSeasonConfig, useResponsive } from '../../../services/hooks';
import { NAVBAR } from '../../../utils/config';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import {
  getPredefinedFilters,
  getModuleViewsAction,
  updateEmployeeConfigAction
} from '../../../services/request';
import {
  setOLOrderFilters,
  setZSOrderFilters,
  setPLOrderFilters,
  setWROrderFilters
} from '../../../services/redux-actions';
import { CircularProgressContainer } from '../../../components/others/CircularProgressContainer';
import { DialogAnimate } from '../../../components/animate';
import { ViewForm } from './ViewForm';
import { FilterTree } from './FilterTree';
import { ViewList } from './ViewList';
import SendMessage from './SendMessage';
import SendMessageL27 from './SendMessageL27';
import GenerateSpreadsheet from './GenerateSpreadsheet';
import WRGenerateSpreadsheet from './WRGenerateSpreadsheet';
import ActionsDropdownButton from './ActionsButton';

export default function OrderSidebar({
  isOpenSidebar,
  onCloseSidebar,
  idList,
  setIdList,
  ordersDataForMessages,
  refreshOrders,
  orderListLoading,
  selectedOrdersIds
}) {
  const filtersRef = React.useRef();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { season, module, employeeId, employeeConfigId, seasonIdentifier } = useSelector(
    state => state.app
  );
  const [reloadConfig, setReloadConfig] = React.useState(false);
  const { orderFilters } = useGetSeasonConfig(module, season, reloadConfig);
  const { loading, payload, error } = useQuery(getPredefinedFilters(season));
  const { mutate } = useMutation(updateEmployeeConfigAction);
  const moduleViewsQuery = useQuery(getModuleViewsAction(module));
  const isDesktop = useResponsive('up', 'md');
  const [predefinedFilters, setPredefinedFilters] = React.useState({});
  const [moduleViews, setModuleViews] = React.useState([]);
  const [isOpenViewModal, setIsOpenViewModal] = React.useState(false);

  const setModuleFilter = (reset = false) => {
    if (module === 'obozy') {
      return setOLOrderFilters({
        [season]: reset
          ? filtersRef.current.resetFilters()
          : filtersRef.current.getFilters()
      });
    } else if (module === 'zielone_szkoly') {
      return setZSOrderFilters({
        [season]: reset
          ? filtersRef.current.resetFilters()
          : filtersRef.current.getFilters()
      });
    } else if (module === 'polkolonie') {
      return setPLOrderFilters({
        [season]: reset
          ? filtersRef.current.resetFilters()
          : filtersRef.current.getFilters()
      });
    } else if (module === 'wyjazdy_rodzinne') {
      return setWROrderFilters({
        [season]: reset
          ? filtersRef.current.resetFilters()
          : filtersRef.current.getFilters()
      });
    }
  };

  React.useEffect(() => {
    if (!error && !loading) {
      setPredefinedFilters(payload);
    }
  }, [loading, season]);

  React.useEffect(() => {
    if (!moduleViewsQuery.error && !moduleViewsQuery.loading) {
      setModuleViews(moduleViewsQuery.payload.results);
    }
  }, [moduleViewsQuery.loading, module]);

  React.useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const moduleFiltersMutationBody = payload => {
    if (module === 'obozy') {
      return { ol_order_filters: payload, employee: employeeId };
    } else if (module === 'zielone_szkoly') {
      return { zs_order_filters: payload, employee: employeeId };
    } else if (module === 'polkolonie') {
      return { pl_order_filters: payload, employee: employeeId };
    } else if (module === 'wyjazdy_rodzinne') {
      return { wr_order_filters: payload, employee: employeeId };
    }
  };

  const handleApplyFilters = async () => {
    const { payload } = dispatch(setModuleFilter());
    const { error } = await mutate(
      moduleFiltersMutationBody(payload),
      employeeConfigId
    );
    setReloadConfig(!reloadConfig);
  };

  const deleteViewCallback = id => {
    setModuleViews(_.filter(moduleViews, obj => obj.id !== id));
  };

  const createViewCallback = view => {
    setModuleViews([view, ...moduleViews]);
  };

  const updateViewCallback = view => {
    setModuleViews(
      moduleViews.map(prevView => (prevView.id === view.id ? view : prevView))
    );
  };

  const resetFilters = async () => {
    const { payload } = dispatch(setModuleFilter(true));
    const { error } = await mutate(
      module == 'obozy'
        ? { ol_order_filters: payload, employee: employeeId }
        : module == 'zielone_szkoly'
          ? { zs_order_filters: payload, employee: employeeId }
          : module == 'wyjazdy_rodzinne'
            ? { wr_order_filters: payload, employee: employeeId }
            : { pl_order_filters: payload, employee: employeeId },
      employeeConfigId
    );
  };

  const getViewDataCallback = id => _.filter(moduleViews, obj => obj.id === id)[0];

  const renderContent = (
    <Scrollbar style={{ opacity: loading ? '0.5' : '1' }}>
      {loading ? <CircularProgressContainer size={50} thickness={2.5} /> : null}
      <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Button
          fullWidth
          variant="contained"
          endIcon={<Iconify icon={'eva:save-fill'} />}
          onClick={() => setIsOpenViewModal(true)}
        >
          Zapisz widok
        </Button>
      </Box>
      {module == 'wyjazdy_rodzinne' ? (
        <WRGenerateSpreadsheet idList={idList} />
      ) : (
        <GenerateSpreadsheet idList={idList} />
      )}
      {seasonIdentifier == 'l_27' ? <SendMessageL27
        idList={idList}
        setIdList={setIdList}
        ordersDataForMessages={ordersDataForMessages}
      /> : <SendMessage
        idList={idList}
        setIdList={setIdList}
        ordersDataForMessages={ordersDataForMessages}
      />}
      {module == 'obozy' && (
        <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
          <ActionsDropdownButton
            idList={idList}
            selectedOrdersIds={selectedOrdersIds}
            onRefresh={refreshOrders}
          />
        </Box>
      )}
      <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Button
          variant="contained"
          fullWidth
          endIcon={<RefreshIcon />}
          onClick={refreshOrders}
          disabled={orderListLoading}
        >
          Odśwież
        </Button>
      </Box>
      <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Button
          sx={{ mr: 2 }}
          fullWidth
          variant="contained"
          onClick={handleApplyFilters}
        >
          Zastosuj filtry
        </Button>
        <Button variant="contained" fullWidth onClick={resetFilters}>
          Zresetuj filtry
        </Button>
      </Box>
      <Divider />
      <List disablePadding>
        {!_.isEmpty(predefinedFilters) ? (
          <FilterTree
            data={predefinedFilters}
            ref={filtersRef}
            initSelected={orderFilters}
          />
        ) : null}
        {!_.isEmpty(moduleViews) ? (
          <ViewList
            data={moduleViews}
            deleteViewCallback={deleteViewCallback}
            getViewDataCallback={getViewDataCallback}
          />
        ) : null}
      </List>
    </Scrollbar>
  );

  return (
    <>
      {isDesktop ? (
        <Drawer
          variant="permanent"
          PaperProps={{
            sx: { width: NAVBAR.BASE_WIDTH + 20, position: 'relative' }
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: NAVBAR.BASE_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}
      <DialogAnimate
        open={isOpenViewModal}
        onClose={() => setIsOpenViewModal(false)}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 99999
        }}
      >
        <ViewForm
          data={moduleViews}
          closeDialog={() => setIsOpenViewModal(false)}
          createViewCallback={createViewCallback}
          updateViewCallback={updateViewCallback}
        />
      </DialogAnimate>
    </>
  );
}

OrderSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onOpenCompose: PropTypes.func,
  onCloseSidebar: PropTypes.func
};
